import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { NotExecutedHistoryItem } from 'src/app/model/history-item/not-executed-history-item';
import ProductAsset from 'src/app/model/product-asset';
import { ClientCompletedDetailSubProject } from 'src/app/model/project/client-completed-detail-sub-project';
import { ClientCompletedSubProject } from 'src/app/model/project/client-completed-sub-project';
import { ProjectPlanningAssign } from 'src/app/model/project/project-planning-assign';
import { Repo } from 'src/app/model/project/repo';
import { SoldRented } from 'src/app/model/project/sold-rented';
import { SubProjectImmoCompleted } from 'src/app/model/project/sub-project-immo-completed';
import { SubProjectImmoDelayed } from 'src/app/model/project/sub-project-immo-delayed';
import { SubProjectImmoNonExecuted } from 'src/app/model/project/sub-project-immo-non-executed';
import { ImmoStudioEmployeeAggregates } from 'src/app/model/tour/immo-studio-employee-aggregates';
import { Adjustment } from '../../model/project/adjustment';
import { ClientPendingDetailSubProject } from '../../model/project/client-pending-detail-sub-project';
import { ClientPendingSubProject } from '../../model/project/client-pending-sub-project';
import { NonArchivedSubProject } from '../../model/project/non-archived-sub-project';
import { SubProjectImmoAppointment } from '../../model/project/sub-project-immo-appointment';
import { SubProjectImmoDesign } from '../../model/project/sub-project-immo-design';
import { SubProjectImmoNew } from '../../model/project/sub-project-immo-new';
import { SubProjectImmoPrint } from '../../model/project/sub-project-immo-print';
import { SubProjectImmoText } from '../../model/project/sub-project-immo-text';
import { ProperPageResult } from '../../model/proper-page-result';
import { SimpleLoggedAction } from '../../model/simple-logged-action';
import { ImmoStudioAssign } from '../../model/tour/immo-studio-assign';
import { ImmoStudioTour } from '../../model/tour/immo-studio-tour';
import { ImmoBillingDetailSubProject } from '../../pages/immo-ip/overview/billing-detail/model/immo-billing-detail-sub-project';
import { ImmoBillingDetailSaveDto } from '../../pages/immo-ip/overview/billing-detail/model/ImmoBillingSaveInvoiceLineDto';
import { ImmoBillingSubProject } from '../../pages/immo-ip/overview/billing/model/immo-billing-sub-project';
import { _delete, _get, _post } from '../base.service';
import { LoadingService } from '../loading.service';
import { SnackService } from '../snack.service';

@Injectable({
  providedIn: 'root',
})
export class SubProjectControllerRequestService {
  private static BASE_URL = '/subprojects';
  private readonly get: <T>(
    path: string,
    deserialize: (response: any) => T
  ) => Observable<T>;
  private readonly post: <T>(
    path: string,
    body: any,
    deserialize: (response: any) => T
  ) => Observable<T>;
  private readonly delete: (path: string) => Observable<void>;
  constructor(
    http: HttpClient,
    snackService: SnackService,
    loadingService: LoadingService,
    router: Router
  ) {
    const requestServices = {
      http,
      loadingService,
      router,
      snackService,
    };
    this.get = _get(
      SubProjectControllerRequestService.BASE_URL,
      requestServices
    );
    this.post = _post(
      SubProjectControllerRequestService.BASE_URL,
      requestServices
    );
    this.delete = _delete(
      SubProjectControllerRequestService.BASE_URL,
      requestServices
    );
  }

  public getNonArchivedSubProjects(
    projectId: number
  ): Promise<NonArchivedSubProject[]> {
    return this.get(
      `/client-non-archived-subprojects/${projectId}`,
      (res) => res as NonArchivedSubProject[]
    ).toPromise();
  }

  public getImmoNew(
    filters: string,
    sort: string,
    sortAscending: boolean,
    pageSize: number,
    page: number,
    showArchived: boolean
  ): Promise<ProperPageResult<SubProjectImmoNew>> {
    const filtersParam: string = `filters=${filters}`;
    const pageableParam: string = `page=${page}&size=${pageSize}&sortDirection=${
      sortAscending ? 'ASC' : 'DESC'
    }&sortProperty=${sort}`;
    const showArchivedParam: string = `showArchived=${showArchived}`;
    const pathParams: string = `?${filtersParam}&${pageableParam}&${showArchivedParam}`;

    return this.get(`/immo/new${pathParams}`, (res) => {
      const result: ProperPageResult<SubProjectImmoNew> =
        new ProperPageResult();
      result.totalItems = res.totalItems;
      result.items = res.items;

      return result;
    }).toPromise();
  }

  public getImmoPrint(
    filters: string,
    sort: string,
    sortAscending: boolean,
    pageSize: number,
    page: number,
    showArchived: boolean
  ): Promise<ProperPageResult<SubProjectImmoPrint>> {
    const filtersParam: string = `filters=${filters}`;
    const pageableParam: string = `page=${page}&size=${pageSize}&sortDirection=${
      sortAscending ? 'ASC' : 'DESC'
    }&sortProperty=${sort}`;
    const showArchivedParam: string = `showArchived=${showArchived}`;
    const pathParams: string = `?${filtersParam}&${pageableParam}&${showArchivedParam}`;

    return this.get(`/immo/print${pathParams}`, (res) => {
      const result: ProperPageResult<SubProjectImmoPrint> =
        new ProperPageResult();
      result.totalItems = res.totalItems;
      result.items = res.items;

      return result;
    }).toPromise();
  }

  public immoPrintRegister(subProjectIds: number[]): Promise<void> {
    const body = {
      subProjectIds: subProjectIds,
    };
    return this.post('/immo/print/register', body, () => {}).toPromise();
  }

  public getImmoText(
    filters: string,
    sort: string,
    sortAscending: boolean,
    pageSize: number,
    page: number,
    showArchived: boolean
  ): Promise<ProperPageResult<SubProjectImmoText>> {
    const filtersParam: string = `filters=${filters}`;
    const pageableParam: string = `page=${page}&size=${pageSize}&sortDirection=${
      sortAscending ? 'ASC' : 'DESC'
    }&sortProperty=${sort}`;
    const showArchivedParam: string = `showArchived=${showArchived}`;
    const pathParams: string = `?${filtersParam}&${pageableParam}&${showArchivedParam}`;

    return this.get(`/immo/text${pathParams}`, (res) => {
      const result: ProperPageResult<SubProjectImmoText> =
        new ProperPageResult();
      result.totalItems = res.totalItems;
      result.items = res.items;

      return result;
    }).toPromise();
  }

  public immoTextRegister(
    ipBoardIdsRegisterText: number[],
    ipBoardIdsRegisterExtraBoard: number[]
  ): Promise<void> {
    const body = {
      ipBoardIdsRegisterText: ipBoardIdsRegisterText,
      ipBoardIdsRegisterExtraBoard: ipBoardIdsRegisterExtraBoard,
    };
    return this.post('/immo/text/register', body, () => {}).toPromise();
  }

  public archive(subProjectId: number): Promise<void> {
    return this.delete(`/archive/${subProjectId}`).toPromise();
  }

  public release(subProjectId: number): Promise<void> {
    return this.post(
      `/immo/release/${subProjectId}`,
      null,
      () => {}
    ).toPromise();
  }

  public setInvoiceAsNonExecuted(subProjectId: number): Promise<void> {
    return this.post(
      `/immo/setInvoiceAsNonExecuted/${subProjectId}`,
      null,
      () => {}
    ).toPromise();
  }

  public getImmoDesign(
    filters: string,
    sort: string,
    sortAscending: boolean,
    pageSize: number,
    page: number,
    showArchived: boolean
  ): Promise<ProperPageResult<SubProjectImmoDesign>> {
    const filtersParam: string = `filters=${filters}`;
    const pageableParam: string = `page=${page}&size=${pageSize}&sortDirection=${
      sortAscending ? 'ASC' : 'DESC'
    }&sortProperty=${sort}`;
    const showArchivedParam: string = `showArchived=${showArchived}`;
    const pathParams: string = `?${filtersParam}&${pageableParam}&${showArchivedParam}`;

    return this.get(`/immo/design${pathParams}`, (res) => {
      const result: ProperPageResult<SubProjectImmoDesign> =
        new ProperPageResult();
      result.totalItems = res.totalItems;
      result.items = res.items;

      return result;
    }).toPromise();
  }

  public getImmoAppointment(
    filters: string,
    sort: string,
    sortAscending: boolean,
    pageSize: number,
    page: number,
    showArchived: boolean,
    noTour: boolean = false
  ): Promise<ProperPageResult<SubProjectImmoAppointment>> {
    const filtersParam: string = `filters=${filters}`;
    const pageableParam: string = `page=${page}&size=${pageSize}&sortDirection=${
      sortAscending ? 'ASC' : 'DESC'
    }&sortProperty=${sort}`;
    const showArchivedParam: string = `showArchived=${showArchived}`;
    const pathParams: string = `?${filtersParam}&${pageableParam}&${showArchivedParam}`;

    return this.get(
      `/immo/appointment-${noTour ? 'no-tour' : 'new'}${pathParams}`,
      (res) => {
        const result: ProperPageResult<SubProjectImmoAppointment> =
          new ProperPageResult();
        result.totalItems = res.totalItems;
        result.items = res.items;

        return result;
      }
    ).toPromise();
  }

  public getImmoDelayed(
    filters: string,
    sort: string,
    sortAscending: boolean,
    pageSize: number,
    page: number,
    showArchived: boolean
  ): Promise<ProperPageResult<SubProjectImmoDelayed>> {
    const filtersParam: string = `filters=${filters}`;
    const pageableParam: string = `page=${page}&size=${pageSize}&sortDirection=${
      sortAscending ? 'ASC' : 'DESC'
    }&sortProperty=${sort}`;
    const showArchivedParam: string = `showArchived=${showArchived}`;
    const pathParams: string = `?${filtersParam}&${pageableParam}&${showArchivedParam}`;

    return this.get(`/immo/delayed${pathParams}`, (res) => {
      const result: ProperPageResult<SubProjectImmoDelayed> =
        new ProperPageResult();
      result.totalItems = res.totalItems;
      result.items = res.items;

      return result;
    }).toPromise();
  }

  public getImmoNonExecuted(
    filters: string,
    sort: string,
    sortAscending: boolean,
    pageSize: number,
    page: number,
    showArchived: boolean
  ): Promise<ProperPageResult<SubProjectImmoNonExecuted>> {
    const filtersParam: string = `filters=${filters}`;
    const pageableParam: string = `page=${page}&size=${pageSize}&sortDirection=${
      sortAscending ? 'ASC' : 'DESC'
    }&sortProperty=${sort}`;
    const showArchivedParam: string = `showArchived=${showArchived}`;
    const pathParams: string = `?${filtersParam}&${pageableParam}&${showArchivedParam}`;

    return this.get(`/immo/nonExecuted${pathParams}`, (res) => {
      const result: ProperPageResult<SubProjectImmoNonExecuted> =
        new ProperPageResult();
      result.totalItems = res.totalItems;
      result.items = res.items;

      return result;
    }).toPromise();
  }

  public getImmoNonExecutedCount(): Promise<number> {
    return this.get(`/immo/nonExecutedCount`, (res) => res).toPromise();
  }

  public getImmoCompleted(
    filters: string,
    sort: string,
    sortAscending: boolean,
    pageSize: number,
    page: number,
    showArchived: boolean
  ): Promise<ProperPageResult<SubProjectImmoCompleted>> {
    const filtersParam: string = `filters=${filters}`;
    const pageableParam: string = `page=${page}&size=${pageSize}&sortDirection=${
      sortAscending ? 'ASC' : 'DESC'
    }&sortProperty=${sort}`;
    const showArchivedParam: string = `showArchived=${showArchived}`;
    const pathParams: string = `?${filtersParam}&${pageableParam}&${showArchivedParam}`;

    return this.get(`/immo/completed${pathParams}`, (res) => {
      const result: ProperPageResult<SubProjectImmoCompleted> =
        new ProperPageResult();
      result.totalItems = res.totalItems;
      result.items = res.items;

      return result;
    }).toPromise();
  }

  public getClientPending(
    companyId: number,
    filters: string,
    sort: string,
    sortAscending: boolean,
    pageSize: number,
    page: number,
    showArchived: boolean
  ): Promise<ProperPageResult<ClientPendingSubProject>> {
    const filtersParam: string = `filters=${filters}`;
    const pageableParam: string = `page=${page}&size=${pageSize}&sortDirection=${
      sortAscending ? 'ASC' : 'DESC'
    }&sortProperty=${sort}`;
    const showArchivedParam: string = `showArchived=${showArchived}`;
    const pathParams: string = `?${filtersParam}&${pageableParam}&${showArchivedParam}`;

    return this.get(`/client/pending/${companyId}${pathParams}`, (res) => {
      const result: ProperPageResult<ClientPendingSubProject> =
        new ProperPageResult();
      result.totalItems = res.totalItems;
      result.items = res.items;

      return result;
    }).toPromise();
  }

  public getClientPendingDetail(
    subProjectId: number
  ): Promise<ClientPendingDetailSubProject> {
    return this.get(
      `/client/pending-detail/${subProjectId}`,
      (res) => res as ClientPendingDetailSubProject
    ).toPromise();
  }

  public getClientCompleted(
    companyId: number,
    filters: string,
    sort: string,
    sortAscending: boolean,
    pageSize: number,
    page: number,
    showArchived: boolean
  ): Promise<ProperPageResult<ClientCompletedSubProject>> {
    const filtersParam: string = `filters=${filters}`;
    const pageableParam: string = `page=${page}&size=${pageSize}&sortDirection=${
      sortAscending ? 'ASC' : 'DESC'
    }&sortProperty=${sort}`;
    const showArchivedParam: string = `showArchived=${showArchived}`;
    const pathParams: string = `?${filtersParam}&${pageableParam}&${showArchivedParam}`;

    return this.get(`/client/completed/${companyId}${pathParams}`, (res) => {
      const result: ProperPageResult<ClientCompletedSubProject> =
        new ProperPageResult();
      result.totalItems = res.totalItems;
      result.items = res.items;

      return result;
    }).toPromise();
  }

  public getClientCompletedDetail(
    projectId: number,
    subProjectId?: number,
    intendNewAdjustment = false
  ): Promise<ClientCompletedDetailSubProject> {
    const url =
      subProjectId != null
        ? `/client/completed-detail/${subProjectId}`
        : `/client/completed-detail-by-project/${projectId}/${intendNewAdjustment}`;
    return this.get(
      url,
      (res) => res as ClientCompletedDetailSubProject
    ).toPromise();
  }

  public postClientCompletedAdjsutment(
    projectId: number,
    adjustment: Adjustment
  ): Promise<number> {
    return this.post(
      `/client/completed-adjustment/${projectId}`,
      adjustment,
      (res) => res
    ).toPromise();
  }

  public postClientCompletedRepo(
    projectId: number,
    repo: Repo
  ): Promise<number> {
    return this.post(
      `/client/completed-repo/${projectId}`,
      repo,
      (res) => res
    ).toPromise();
  }

  public postClientCompletedSoldRented(
    projectId: number,
    soldRented: SoldRented
  ): Promise<number> {
    return this.post(
      `/client/completed-sold-rented/${projectId}`,
      soldRented,
      (res) => res
    ).toPromise();
  }

  //TODO putting this here for now. I think we'd do good to create a separate RestController (and frontend service) for all Immo endpoints,
  //decoupling it from being based on SubProject or any other Entity model
  public getImmoStudio(
    filters: string,
    pageSize: number,
    page: number,
    includeUnassigned: boolean,
    atelierDate: string
  ): Promise<ProperPageResult<ImmoStudioTour>> {
    const filtersParam: string = `filters=${filters}`;
    const pageableParam: string = `page=${page}&size=${pageSize}&includeUnassigned=${includeUnassigned}&atelierDate=${atelierDate}`;

    const pathParams: string = `?${filtersParam}&${pageableParam}`;

    return this.get(`/immo/studio${pathParams}`, (res) => {
      const result: ProperPageResult<ImmoStudioTour> = new ProperPageResult();
      result.totalItems = res.totalItems;
      result.items = res.items;

      return result;
    }).toPromise();
  }

  public getImmoStudioEmployeeAggregates(
    atelierDate: string
  ): Promise<ImmoStudioEmployeeAggregates[]> {
    return this.get(
      `/immo/studio-employee-aggregates?atelierDate=${atelierDate}`,
      (res) => res
    ).toPromise();
  }

  public updateImmoStudioAssignees(
    assignDto: ImmoStudioAssign
  ): Promise<ImmoStudioTour> {
    return this.post(
      `/immo/studio`,
      assignDto,
      (res) => res as ImmoStudioTour
    ).toPromise();
  }

  public saveImmoBillingInvoices(
    billing: ImmoBillingDetailSaveDto[]
  ): Promise<void> {
    return this.post(
      '/immo/billing-detail/save',
      billing,
      (res) => res
    ).toPromise();
  }

  public getImmoBilling(
    filters: string,
    sort: string,
    sortAscending: boolean,
    pageSize: number,
    page: number
  ): Promise<ProperPageResult<ImmoBillingSubProject>> {
    const filtersParam: string = `filters=${filters}`;
    const pageableParam: string = `page=${page}&size=${pageSize}&sortDirection=${
      sortAscending ? 'ASC' : 'DESC'
    }&sortProperty=${sort}`;
    const pathParams: string = `?${filtersParam}&${pageableParam}`;

    return this.get(`/immo/billing${pathParams}`, (res) => {
      const result: ProperPageResult<ImmoBillingSubProject> =
        new ProperPageResult();
      result.totalItems = res.totalItems;
      result.items = res.items;

      return result;
    }).toPromise();
  }

  public getImmoBillingDetail(id: number): Promise<ImmoBillingDetailResponse> {
    return this.get(
      `/immo/billing-detail/${id}`,
      (res) => res as ImmoBillingDetailResponse
    ).toPromise();
  }

  public markBillingReady(
    id: number,
    value: boolean
  ): Promise<SimpleLoggedAction> {
    const body = {
      subProjectId: id,
      value: value,
    };

    return this.post(
      '/billing-ready',
      body,
      (res) => res as SimpleLoggedAction
    ).toPromise();
  }

  public assignPlanning(
    subProjectId: number,
    assignDto: ProjectPlanningAssign
  ): Promise<ProjectPlanningAssign> {
    return this.post(
      `/assignPlanning/${subProjectId}`,
      assignDto,
      (res) => res as ProjectPlanningAssign
    ).toPromise();
  }
}

export interface ImmoBillingDetailResponse {
  subProject: ImmoBillingDetailSubProject;
  baseProject?: ImmoBillingDetailSubProject;
  notExecutedHistoryItem?: NotExecutedHistoryItem;
  latestExecutionAssets?: ProductAsset[];
}
